import * as React from 'react';
import { useEventCallback, useForkRef, useIsomorphicEffect } from '../../hooks';
import { useFieldBase } from '../FieldBase/FieldBase.context';

export interface InputBaseProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'width'> {
    /**
     * The default value. Use when the component is not controlled.
     */
    defaultValue?: string;

    /**
     * If `true`, the component will be disabled.
     */
    disabled?: boolean;

    /**
     * If `true`, the `input` element will have the `aria-invalid` attribute.
     */
    error?: boolean;

    /**
     * Callback fired when the `input` element loses focus.
     * @param {React.FocusEvent} event The event source of the callback.
     */
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;

    /**
     * Callback fired when the value of the `input` element is changed.
     * @param {React.ChangeEvent} event The event source of the callback.
     */
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;

    /**
     * Callback fired when the `input` element receives focus.
     * @param {React.FocusEvent} event The event source of the callback.
     */
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;

    /**
     * The `tabindex` attribute of the `input` element.
     * @default 0
     */
    tabIndex?: number;

    /**
     * The value of the `input` element. Required if the component is controlled.
     */
    value?: string;

    /**
     * @internal
     * This prop is used internally by FieldBase. Do not set it manually.
     */
    isFieldBaseControl?: boolean;

    /**
     * Allow any data-* attribute.
     */
    [key: `data-${string}`]: string;
}

export const InputBase = React.forwardRef(function InputBase(
    props: InputBaseProps,
    ref: React.ForwardedRef<HTMLInputElement>
): React.JSX.Element {
    const {
        disabled,
        error,
        onBlur,
        onChange,
        onFocus,
        tabIndex = 0,
        value: valueProp,
        isFieldBaseControl = false,
        ...other
    } = props;

    const { setFilled, setFocused, controlRef } = useFieldBase();
    const { current: isControlled } = React.useRef(valueProp !== undefined);

    const elementRef = React.useRef<HTMLInputElement | null>(null);
    const handleRefIntermediary = useForkRef(
        elementRef,
        isFieldBaseControl ? (controlRef as ((instance: HTMLInputElement | null) => void) | null) : null
    );
    const handleRef = useForkRef(handleRefIntermediary, ref);

    React.useEffect(() => {
        // Handle cases where the autoFocus prop is used, as this will not neccessarliy trigger onFocus to fire.
        if (document && document.activeElement === elementRef.current) {
            setFocused?.(true);
        }
    }, [setFocused]);

    const handleBlur = useEventCallback((event: React.FocusEvent<HTMLInputElement>): void => {
        onBlur?.(event);
        setFocused?.(false);
    });

    const handleFocus = useEventCallback((event: React.FocusEvent<HTMLInputElement>): void => {
        onFocus?.(event);
        setFocused?.(true);
    });

    const checkFilled = React.useCallback(
        (value: string | undefined) => {
            setFilled?.(value !== '');
        },
        [setFilled]
    );

    useIsomorphicEffect(() => {
        if (isControlled) {
            checkFilled(valueProp);
        }
    }, [valueProp, checkFilled, isControlled]);

    const handleChange = useEventCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        if (!isControlled) {
            checkFilled(event.target.value);
        }
        onChange?.(event);
    });

    return (
        <input
            ref={handleRef}
            aria-invalid={error || undefined}
            disabled={disabled || undefined}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            tabIndex={tabIndex}
            value={valueProp}
            data-input-base={true}
            {...other}
        />
    );
});
